import { useState } from "react";
import { Check, ArrowRight, TrendingUp, Megaphone, Globe, LifeBuoy, Zap, Building2, Crown, Star } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero, SectionTitle } from "@/components/blocks";
import { IMAGES } from "@/data/content";
import { useCentre } from "@/context/CentreContext";

const FIELD = "w-full bg-hp-dark border border-white/10 px-4 py-3 text-sm text-white placeholder:text-neutral-500 focus:outline-none focus:border-hp-yellow transition-colors";
const LABEL = "block text-xs font-bold tracking-widest uppercase text-neutral-400 mb-2";

const ADVANTAGES = [
  { icon: Zap, t: "Image de marque", d: "Une identité forte, premium et reconnue dans l'univers de la performance." },
  { icon: LifeBuoy, t: "Accompagnement", d: "Formation, méthodologie et support technique continus." },
  { icon: Building2, t: "File Service", d: "Accès à Horsepower Tuning Files et à nos ingénieurs moteur." },
  { icon: Megaphone, t: "Communication", d: "Une stratégie de communication et de contenu partagée." },
  { icon: Globe, t: "Visibilité nationale", d: "Un site internet et une présence à l'échelle du réseau." },
  { icon: TrendingUp, t: "Acquisition client", d: "Un site orienté conversion qui génère des demandes qualifiées." },
];

const PACKS = [
  {
    name: "Pack Franchisé Horsepower", price: "699 €", unit: "HT", highlight: true,
    features: [
      "Accès serveur Horsepower Tuning Files",
      "Site Web personnalisé avec prise de RDV et renseignements en direct",
      "Compte Horsepower Tuning Parts avec les tarifs négociés",
      "Merch (cartes de visite, prospectus, fardes, porte-clés, autocollants)",
      "Page Instagram & gestion Meta et Google Ads",
      "Événements & vidéos YouTube",
      "Conseiller Horsepower Tuning Parts dédié",
      "Accès à un groupe WhatsApp avec les tuners",
    ],
  },
];

const WHY = [
  { t: "Un savoir-faire reconnu", d: "Notre partenariat avec des acteurs reconnus du milieu et l'expertise de nos ingénieurs vous assurent un accès à des techniques avancées et des services de qualité supérieure." },
  { t: "Une équipe à vos côtés", d: "De la formation initiale à l'accompagnement continu, nous serons présents à chaque étape de votre parcours pour garantir votre réussite." },
  { t: "Des opportunités de croissance", d: "En tant que membre, vous aurez accès à un réseau de partenaires et de clients fidèles, avec des opportunités constantes pour développer votre business." },
];

export default function Franchise() {
  const { centres } = useCentre();
  const [submitting, setSubmitting] = useState(false);
  const [f, setF] = useState({ first_name: "", last_name: "", phone: "", email: "", city_dept: "", company: "", activity: "", has_garage: false, has_dyno: false, message: "", consent: false });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent) return toast.error("Merci d'accepter d'être recontacté.");
    if (!f.first_name || !f.phone || !f.email) return toast.error("Nom, téléphone et email sont requis.");
    setSubmitting(true);
    try {
      await api.post("/franchise", f);
      toast.success("Votre projet a bien été envoyé ! Notre équipe réseau vous recontacte.");
      setF({ first_name: "", last_name: "", phone: "", email: "", city_dept: "", company: "", activity: "", has_garage: false, has_dyno: false, message: "", consent: false });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="franchise-page">
      <Seo title="Devenir franchisé Horsepower | Rejoignez le réseau national"
        description="Ouvrez votre centre Horsepower : image de marque premium, File Service, accompagnement, communication et acquisition client. Rejoignez le réseau national de reprogrammation moteur."
        canonical="https://www.horsepowergarage.fr/devenir-franchise" />
      <PageHero eyebrow="Développement réseau" title="Devenez franchisé Horsepower"
        subtitle="Rejoignez un réseau national premium spécialisé dans la performance automobile. Une marque, une méthode, un accompagnement complet."
        image={IMAGES.sportsDark} tall />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <SectionTitle eyebrow="Rejoignez le réseau" title="Pourquoi ouvrir un centre Horsepower ?" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {ADVANTAGES.map((a, i) => (
            <Reveal key={a.t} delay={(i % 3) * 0.06}>
              <div className="hp-card p-7 h-full">
                <a.icon className="text-hp-yellow mb-4" size={28} />
                <h3 className="font-display font-bold uppercase tracking-tight text-lg">{a.t}</h3>
                <p className="text-neutral-400 text-sm mt-2 leading-relaxed">{a.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PACKS */}
      <section className="section-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
          <SectionTitle onLight center eyebrow="Les offres de franchise Horsepower" title="Un pack unique, tout inclus"
            sub="Que vous soyez un centre établi cherchant à élargir vos services ou un passionné de performance moteur prêt à se lancer, le pack franchisé Horsepower vous donne tous les outils pour réussir." />
          <div className="grid gap-6 mt-12 max-w-xl mx-auto">
            {PACKS.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.1}>
                <div className={`relative h-full p-8 rounded ${p.highlight ? "bg-hp-dark text-white border-2 border-hp-gold" : "bg-white border border-hp-dark/10"}`} data-testid={`pack-${i}`}>
                  {p.highlight && <span className="absolute -top-3 right-6 flex items-center gap-1 bg-hp-gold text-hp-dark text-[10px] font-bold uppercase tracking-wider px-3 py-1"><Crown size={12} /> Tout inclus</span>}
                  <h3 className={`font-display uppercase tracking-tight text-2xl ${p.highlight ? "text-white" : "text-hp-dark"}`}>{p.name}</h3>
                  <div className="flex items-baseline gap-2 mt-3">
                    <span className={`font-display text-5xl ${p.highlight ? "text-hp-yellow" : "text-hp-dark"}`}>{p.price}</span>
                    <span className={`text-sm font-light3 ${p.highlight ? "text-white/60" : "text-hp-dark/50"}`}>{p.unit} / mois</span>
                  </div>
                  {p.note && <p className="text-hp-gold text-xs font-bold uppercase tracking-wide mt-1">{p.note}</p>}
                  <ul className="mt-6 space-y-3">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm">
                        <Check className={`shrink-0 mt-0.5 ${p.highlight ? "text-hp-yellow" : "text-hp-gold"}`} size={18} />
                        <span className={p.highlight ? "text-white/85 font-light3" : "text-hp-dark/75 font-light3"}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <a href="#franchise-form" className={`mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm rounded-sm ${p.highlight ? "hp-cta" : "hp-gold"}`} data-testid={`pack-cta-${i}`}>
                    Choisir ce pack <ArrowRight size={16} />
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="text-center text-hp-dark/45 text-xs mt-6 font-light3">Tarifs indicatifs issus de la brochure partenaires. Conditions détaillées communiquées lors de l'étude de votre projet.</p>
        </div>
      </section>

      {/* POURQUOI NOUS CHOISIR */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
        <SectionTitle eyebrow="Pourquoi nous choisir" title="Une structure qui allie passion, innovation et support" />
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {WHY.map((w, i) => (
            <Reveal key={w.t} delay={i * 0.08}>
              <div className="hp-card p-7 h-full">
                <span className="inline-block h-1 w-10 bg-hp-yellow mb-5" />
                <h3 className="font-display uppercase tracking-tight text-lg">{w.t}</h3>
                <p className="text-white/60 mt-3 text-sm leading-relaxed font-light3">{w.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="text-center text-white/70 mt-10 max-w-2xl mx-auto font-light3">
          Faites le choix d'une structure qui allie passion automobile, innovation et support de qualité.
          <span className="text-hp-yellow font-medium"> Ensemble, nous construirons le futur de Horsepower Garage.</span>
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="hp-card p-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
          <span className="label-eyebrow">Ils font déjà partie du réseau</span>
          {centres.map((c) => (
            <span key={c.slug} className="font-display font-bold uppercase tracking-tight text-xl text-neutral-300">{c.city}</span>
          ))}
        </div>
      </section>

      <section id="franchise-form" className="max-w-3xl mx-auto px-4 sm:px-6 py-16 scroll-mt-28">
        <div className="hp-card overflow-hidden">
          <div className="px-6 py-5 border-b border-white/10 bg-black">
            <p className="label-eyebrow">Formulaire</p>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl mt-1">Je veux ouvrir un centre Horsepower</h2>
          </div>
          <form onSubmit={submit} className="p-6 space-y-5" data-testid="franchise-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={LABEL}>Prénom *</label><input className={FIELD} value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="fr-first-name" /></div>
              <div><label className={LABEL}>Nom</label><input className={FIELD} value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="fr-last-name" /></div>
              <div><label className={LABEL}>Téléphone *</label><input className={FIELD} value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="fr-phone" /></div>
              <div><label className={LABEL}>Email *</label><input type="email" className={FIELD} value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="fr-email" /></div>
              <div><label className={LABEL}>Ville / Département</label><input className={FIELD} value={f.city_dept} onChange={(e) => set("city_dept", e.target.value)} data-testid="fr-city" /></div>
              <div><label className={LABEL}>Société actuelle</label><input className={FIELD} value={f.company} onChange={(e) => set("company", e.target.value)} data-testid="fr-company" /></div>
              <div className="sm:col-span-2"><label className={LABEL}>Activité actuelle</label><input className={FIELD} value={f.activity} onChange={(e) => set("activity", e.target.value)} data-testid="fr-activity" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="flex items-center gap-3 hp-card p-4 cursor-pointer"><input type="checkbox" checked={f.has_garage} onChange={(e) => set("has_garage", e.target.checked)} className="h-5 w-5 accent-[#FFED00]" data-testid="fr-has-garage" /><span className="text-sm text-neutral-200">Je possède déjà un garage</span></label>
              <label className="flex items-center gap-3 hp-card p-4 cursor-pointer"><input type="checkbox" checked={f.has_dyno} onChange={(e) => set("has_dyno", e.target.checked)} className="h-5 w-5 accent-[#FFED00]" data-testid="fr-has-dyno" /><span className="text-sm text-neutral-200">Je possède un banc de puissance</span></label>
            </div>
            <div><label className={LABEL}>Message</label><textarea rows={3} className={FIELD} value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="fr-message" /></div>
            <label className="flex items-start gap-3 cursor-pointer text-sm text-neutral-300">
              <input type="checkbox" checked={f.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-0.5 h-5 w-5 accent-[#FFED00]" data-testid="fr-consent" />
              <span>J'accepte d'être recontacté par Horsepower Garage. *</span>
            </label>
            <button type="submit" disabled={submitting} className="hp-cta w-full px-6 py-4 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="fr-submit">
              <Check size={18} /> {submitting ? "Envoi..." : "Envoyer mon projet"} <ArrowRight size={16} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
